import type { SEOProps } from '@/types'

interface FullSEOProps extends SEOProps {
  url?: string
  siteName?: string
}

const DEFAULT_OG_IMAGE = '/og-image.png'
const SITE_NAME = 'WealthSpace'
const BASE_URL = 'https://www.wealthspace.co.uk'

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  url,
  siteName = SITE_NAME,
}: FullSEOProps) {
  const fullTitle = title.includes('WealthSpace') ? title : `${title} | WealthSpace`
  const canonicalUrl = canonical || (url ? `${BASE_URL}${url}` : undefined)
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`

  return (
    <>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:secure_url" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="WealthSpace" />
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="United Kingdom" />
    </>
  )
}
